import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

import { useAppDispatch, useAppSelector } from '../app/hooks';
import '../App.css';
import { fetchPlans, Plan } from '../features/plans/plans';
import { externalPlanIdsSummary, networkLabel, networkOf } from '../features/plans/planNetwork';
import { inListOrder } from '../features/plans/planOrder';
import { RootState } from '../app/store';

const Muted = ({ children }: { children: React.ReactNode }) => (
  <Typography variant="body2" color="text.secondary">{children}</Typography>
);

// Evening speeds are configured per access technology (passive vs active
// ethernet); plan cards show the pair matching the customer's building.
const eveningSpeeds = (plan: Plan) => {
  const set = plan.evening_download_passive > 0 || plan.evening_upload_passive > 0
    || plan.evening_download_ae > 0 || plan.evening_upload_ae > 0;
  if (!set) return <Muted>not set</Muted>;
  return (
    <>
      <Typography variant="body2">
        {`passive ${plan.evening_download_passive} / ${plan.evening_upload_passive} Mbps`}
      </Typography>
      <Typography variant="body2">
        {`AE ${plan.evening_download_ae} / ${plan.evening_upload_ae} Mbps`}
      </Typography>
    </>
  );
};

// Read-only list of plans. Editing happens on the plan card
// (/dashboard/plans/:planId) behind an explicit confirmation step, so a stray
// click or keystroke here can never mutate a plan.
const Plans = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const plansState = useAppSelector((state: RootState) => state.plans);

  useEffect(() => { dispatch(fetchPlans()); }, []);

  const plans = inListOrder(plansState.value);
  const loading = plansState.status === 'loading' && plans.length === 0;

  // Asymmetric plans are shown as download/upload, symmetric ones as one figure.
  const planSpeed = (plan: Plan) => (
    plan.upload > 0 && plan.upload !== plan.speed
      ? `${plan.speed}/${plan.upload} Mbps`
      : `${plan.speed} Mbps`
  );

  const networkChip = (plan: Plan) => {
    const network = networkOf(plan);
    if (network === 'dgtek') return null;
    return <Chip label={networkLabel(network)} size="small" color="secondary" variant="filled" />;
  };

  const skuSummary = (plan: Plan) => {
    const summary = externalPlanIdsSummary(plan.external_plan_ids);
    if (summary === 'none') return <Muted>—</Muted>;
    return <Typography variant="body2">{summary}</Typography>;
  };

  const couponChip = (plan: Plan) => {
    if (!plan.embedded_coupon) return <Muted>—</Muted>;
    return (
      <Chip
        label={plan.embedded_coupon}
        size="small"
        variant={plan.apply_embedded_coupon ? 'filled' : 'outlined'}
        color={plan.apply_embedded_coupon ? 'primary' : 'default'}
        title={plan.apply_embedded_coupon ? 'Applied to every signup on this plan' : 'Set but switched off'}
      />
    );
  };

  // Shows the promo code in the same way as the plan coupon. A filled chip
  // shows a code that is on. An outlined chip shows a code that is set but off.
  const promoCodeChip = (plan: Plan) => {
    if (!plan.promo_code) return <Muted>—</Muted>;
    return (
      <Chip
        label={plan.promo_code}
        size="small"
        variant={plan.apply_promo_code ? 'filled' : 'outlined'}
        color={plan.apply_promo_code ? 'primary' : 'default'}
        title={plan.apply_promo_code
          ? 'Applied when the customer brings the code'
          : 'Set but switched off'}
      />
    );
  };

  const rrp = (plan: Plan) => {
    if (!plan.rrp || Number(plan.rrp) === 0) return <Muted>not set</Muted>;
    return (
      <>
        <Typography variant="body2">{`$${plan.rrp}`}</Typography>
        <Typography variant="caption" color="text.secondary">per month</Typography>
      </>
    );
  };

  const visibleFor = (plan: Plan) => (
    <Stack direction="row" spacing={0.5}>
      {plan.addressLIT && <Chip label="LIT" size="small" variant="outlined" />}
      {plan.addressNonLIT && <Chip label="non-LIT" size="small" variant="outlined" />}
      {plan.addressFTTP && <Chip label="FTTP/HFC" size="small" variant="outlined" />}
      {!plan.addressLIT && !plan.addressNonLIT && !plan.addressFTTP && <Muted>hidden</Muted>}
    </Stack>
  );

  const published = plans.filter((p) => p.published);
  const unpublished = plans.filter((p) => !p.published);

  // One table per group, so what customers can buy is never mixed in with
  // what they cannot. The unpublished one is set back: outlined heading
  // chip, greyed rows.
  const plansTable = (title: string, about: string, rows: Plan[]) => {
    const live = rows === published;
    return (
      <Box component="section" aria-label={title}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{title}</Typography>
          <Chip
            label={rows.length}
            size="small"
            color={live ? 'success' : 'default'}
            variant={live ? 'filled' : 'outlined'}
          />
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          {about}
        </Typography>
        {rows.length === 0 ? (
          <Paper variant="outlined" sx={{ p: 3 }}>
            <Muted>{live ? 'No plan is published.' : 'Nothing here.'}</Muted>
          </Paper>
        ) : (
          <TableContainer
            component={Paper}
            variant="outlined"
            sx={live ? { borderLeft: 4, borderLeftColor: 'success.main' } : { bgcolor: 'action.hover' }}
          >
            <Table sx={{ '& td, & th': { whiteSpace: 'nowrap' } }}>
              <TableHead>
                <TableRow>
                  <TableCell>Plan</TableCell>
                  <TableCell>Displayed price</TableCell>
                  <TableCell>RRP</TableCell>
                  <TableCell>Speed</TableCell>
                  <TableCell>Coupon applied</TableCell>
                  <TableCell>Promo code applied</TableCell>
                  <TableCell>Evening speeds</TableCell>
                  <TableCell>Product ids</TableCell>
                  <TableCell>Visible for</TableCell>
                  <TableCell aria-label="open" />
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((plan) => (
                  <TableRow
                    key={plan.id}
                    hover
                    onClick={() => navigate(`/dashboard/plans/${encodeURIComponent(plan.id)}`)}
                    sx={{ cursor: 'pointer', opacity: live ? 1 : 0.75 }}
                  >
                    <TableCell>
                      <Stack direction="row" spacing={0.75} alignItems="center">
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {plan.name || plan.id}
                        </Typography>
                        {networkChip(plan)}
                      </Stack>
                      <Typography variant="caption" color="text.secondary">
                        {`${plan.id} · ${plan.rsp}`}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{`$${plan.cost}`}</Typography>
                      <Typography variant="caption" color="text.secondary">per month</Typography>
                    </TableCell>
                    <TableCell>{rrp(plan)}</TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{planSpeed(plan)}</Typography>
                    </TableCell>
                    <TableCell>{couponChip(plan)}</TableCell>
                    <TableCell>{promoCodeChip(plan)}</TableCell>
                    <TableCell>{eveningSpeeds(plan)}</TableCell>
                    <TableCell>{skuSummary(plan)}</TableCell>
                    <TableCell>{visibleFor(plan)}</TableCell>
                    <TableCell align="right" sx={{ color: 'text.disabled', width: 40 }}>
                      <ChevronRightIcon fontSize="small" />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    );
  };

  return (
    <Box sx={{
      maxWidth: 1200, mx: 'auto', mt: 10, px: 2,
    }}
    >
      <Toolbar disableGutters sx={{ mb: 1, display: 'flex', justifyContent: 'space-between' }}>
        <Stack direction="row" spacing={1.5} alignItems="baseline">
          <Typography variant="h6">Plans</Typography>
          {plans.length > 0 && (
            <Typography variant="body2" color="text.secondary">
              {`${plans.filter((p) => p.published).length} published · ${plans.length} total`}
            </Typography>
          )}
        </Stack>
        <Button
          color="primary"
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/dashboard/plans/new')}
        >
          Add Plan
        </Button>
      </Toolbar>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 8 }}>
          <CircularProgress />
        </Box>
      )}

      {!loading && plans.length === 0 && (
        <Paper variant="outlined" sx={{ p: 6, textAlign: 'center' }}>
          <Typography color="text.secondary" sx={{ mb: 2 }}>No plans yet.</Typography>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => navigate('/dashboard/plans/new')}
          >
            Add the first plan
          </Button>
        </Paper>
      )}

      {!loading && plans.length > 0 && (
        <Stack spacing={4}>
          {plansTable(
            'Published plans',
            'Shown to customers on the signup site.',
            published,
          )}
          {plansTable(
            'Unpublished plans',
            'Drafts and retired plans. Customers do not see these.',
            unpublished,
          )}
        </Stack>
      )}
    </Box>
  );
};

export default Plans;
